import { IPaymentStatus, IPaymentType } from '../../types/payment'
import { IAnswer } from '../event-registered-users/event-registered-users.interface'
import { IHorseData } from '../horse/horse.interface'
import { IScoreCardSection, IScoreCardType } from '../score-cards/score-cards.interface'

export const HORSE_NAME_DEFAULT = 'No Horse'

export interface ISheetSection {
  name: string
  max: number
  type: string
  rows: {
    number?: number
    description: string
    score: number | string
    coefficient?: number
    remarks?: string
    fraction?: string
  }[]
  notice?: string
}

export interface IJudge {
  id: string
  name: string
  picture: string

  furtherRemarks: string

  // related with dummy sheets score cards
  errors?: number
  sections: ISheetSection[]

  // related with 'hunter-jumper' score cards
  customSections: IScoreCardSection[]
  showJumpOff?: boolean
  fenceHeight?: string

  scoreCardType: 'optimum' | 'fastest' | null

  signatureDate: Date | string | null
  signatureUrl: string | null
}

export type IScoreSheet = {
  id: string
  name: string
  type: IScoreCardType
}

export type IScoreStatus =
  | 'Did Not Place'
  | 'Displaced'
  | 'Eliminated'
  | 'HC'
  | 'Fall'
  | 'No Show'
  | 'Retired'
  | 'Refusal'
  | 'Scratched'
  | 'Withdrew'
  | 'Off Pattern'

export type ScoreTypes = {
  totalPoints?: number | string | null
  judges?: IJudge[] | null
  sheet?: IScoreSheet | null
  details?: string | null
  rank?: number | string | null
  file?: string | null
  earning?: string | null
  scoreStatus?: IScoreStatus | null
}

export interface IHorseSelected {
  id: string
  name: string
  picture: string
}

export interface IRegistrationByDayInterface {
  v?: number
  /** @info Id of the event in which the competitor is registering */
  eventId: string | null
  /** @info Name of the event this document is related to */
  eventName?: string | null
  /** @info Id of the registration to which this registration belongs */
  registrationDocId: string | null
  /** @info Id of the user creating this registration --[COMPETITOR] */
  userId: string | null
  /** @info name of registrar */
  userName?: string | null
  /** @info `true` specifies that the refund amount have been added in the recipient doc */
  refundAmountAdded?: boolean
  /** @info Id of the team member being registered as rider as per this registration */
  riderId: string | null
  /** @info Email id of the person who have been added under horse as rider */
  riderEmail: string | null
  /** @info Name of the person who have been added under horse as rider */
  riderName: string | null
  /** @info Dob of the person who have been added under horse as rider */
  riderDob: Date | string | null
  /** @inteface IUserInterface `userNameNGram` */
  riderNameNGram: string[]
  /** @info Profile picture of the rider */
  riderProfilePicture: string | null
  /** @info country of the rider */
  riderCountry: string | null
  /** @info To be used on client side side only, update true means the doc needs and update */
  update?: boolean
  /** @info To be used on client side side only, teamMembersAvailable true means team members were added against the rider*/
  teamMembersAvailable?: boolean
  /** @info item paid or no */
  paymentStatus: IPaymentStatus
  /** @info how item was paid */
  paymentType: IPaymentType
  /** @info "memo" is optional field when a payment type is "check" */
  checkMemo?: string | null
  /** @info "memo" is required field when a payment type is "check" */
  checkNumber?: string | null
  /** @info Specifies when item was paid */
  paidDate?: Date | string | null
  /** @info card number used for payment  */
  cardNo?: string | null
  /** @info Specifies wether the item is scratched or not */
  isScratched: boolean
  /** @info Specifies how much amount is scratched */
  amountScratched: number
  /** @info Specifies when item was scratched */
  scratchedDate?: Date | null
  /** @info Invoice id of the invoice generated which payment */
  invoiceId?: string | null
  /** @info Invoice url of the invoice generated which payment */
  invoiceUrl?: string | null
  /** @info Amount that is refunded, Once the item is scratched the amount can be refunded, max value should be equal to `registrationPrice` */
  amountRefunded: number
  /** @info Specifies when item was refunded */
  refundedDate?: Date | null
  /** @info The id of the document in `Refund Collection` */
  refundDocId: string | null
  /** @info The id of the refund request from stripe */
  refundId: string | null
  /** @info if the payment is refunded then this will be `refunded` else `null` */
  refundStatus: 'refunded' | null
  /** @info if the payment is refunded then this will be `refunded` else `null` */
  score: ScoreTypes | null
  /** "Order Of Go" Operations tab */
  order: number
  /** @info ride time for rider mode scheduling */
  rideTime?: string | null
  /** @info ride time for class mode scheduling (only on FE without saving to database) */
  rideTimeScheduleByClass?: string | null
  /**
   * @info Out-of-turn flag for rider mode scheduling.
   * true = manual ride time, false = calculated time sequence.
   */
  isOutOfTurn?: boolean | null
  orderOfGoScratched?: boolean
  /** @info Specifies back number */
  backNumber: number
  /** @info Id of the recipient who have paid the amount for this registration */
  recipientId: string | null
  /** @info Name of the recipient */
  recipientName: string | null
  /** @info Profile picture of the recipient */
  recipientProfilePicture: string | null
  /** @info the horse related with rider */
  horsesSelected: IHorseSelected[]
  /** @info Actual id of the horse in horses table */
  horseId: string | null
  /** @info Name of the horse */
  horseName: string | null
  /** @info Profile pic of horse */
  horseProfilePicture?: IHorseData['horseProfilePicture'] | null
  /** @info price for the event to which this registration belongs */
  registrationPrice: number | null
  /** @info determines whether sales tax should be added or not */
  includeSalesTax: boolean
  /** @info original class price */
  originalRegistrationPrice?: number | null
  qualifyFee: string | null
  qualifyFeeName: string | null
  breakTime: number | null
  isBreak?: boolean | null
  isQualify?: boolean | null
  /** @info registrationName for the event to which this registration belongs */
  registrationByDayName: string | null
  /** @info Id of the doc in db */
  id?: string | null
  answers?: IAnswer[]
  /** @info Time of document creation in db */
  created?: Date | string | null
  /** @info Time of document modification in db */
  modified?: Date | string | null
  /** @info if we have divisionUniqId it means it is division child class */
  divisionUniqId?: string | null
  /** @info if we have divisionUuid it means it is class inside division */
  divisionUuid?: string | null
  /** @info if we have isDivision as true it means it is division parent class */
  isDivision?: boolean | null
  uuid: string | null
  uniqId?: string | null
}
